// DialogForm — the dialog CONTAINER for the shared form shell (ADR-038 §1), the
// twin of DrawerForm over a Modal: ONE persisted field (a name, a caption, a label).
// Two fields or more is a drawer; a decision with the inputs that qualify it is
// ConfirmDialog. Same promises as the drawer: every open re-seeds from the row as it
// is then (the stamp with the values — ADR-084 §6), every close path through the
// dirty guard, the refusal line in the body's one slot, the discard ConfirmDialog a
// sibling of the Modal.

import { useEffect, type ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Modal } from '../../ui/Modal.js';
import { FormBody, FormFooter } from './FormShell.js';
import type { UseFormShellReturn } from './useFormShell.js';

export interface DialogFormProps<TValues extends FieldValues> {
  shell: UseFormShellReturn<TValues>;
  open: boolean;
  /** The raw close action. Every close path routes through the dirty guard first. */
  onClose: () => void;
  title: string;
  description?: string;
  /** The submit verb ("Rename", "Save") — i18n'd by the caller. */
  submitLabel: string;
  cancelLabel?: string;
  /** Holds Save while a choice the form shows as blocked stands (a decision form disables, never surprise-fails). */
  submitDisabled?: boolean;
  /** The one field — registers / Controller against shell.form. */
  children: ReactNode;
  /** Tailwind max-w-* for the card; falls back to Modal's default. */
  maxWidth?: string;
  /** Field name to focus + text-select when the dialog opens (the open-to-edit pattern). */
  autoFocusField?: string;
}

export function DialogForm<TValues extends FieldValues>({
  shell,
  open,
  onClose,
  title,
  description,
  submitLabel,
  cancelLabel,
  submitDisabled,
  children,
  maxWidth,
  autoFocusField,
}: DialogFormProps<TValues>) {
  // Every open re-seeds from the row as it is now (a passive effect, after the shell has taken this render's row).
  const { reseed } = shell;
  useEffect(() => {
    if (open) reseed();
  }, [open, reseed]);

  useEffect(() => {
    if (!open || !autoFocusField) return;
    const id = window.setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>(`[role="dialog"] [name="${autoFocusField}"], [role="dialog"] [data-field="${autoFocusField}"]`);
      if (el) {
        el.focus();
        el.select?.();
      }
    }, 0);
    return () => clearTimeout(id);
  }, [open, autoFocusField]);

  return (
    <>
      <Modal
        open={open}
        onClose={() => (shell.gone ? shell.discardAndLeave(onClose) : shell.requestLeave(onClose))}
        title={title}
        description={description}
        maxWidth={maxWidth}
        footer={<FormFooter shell={shell} onCancel={onClose} submitLabel={submitLabel} cancelLabel={cancelLabel} submitDisabled={submitDisabled} />}
      >
        <FormBody shell={shell} onGone={onClose}>
          {children}
        </FormBody>
      </Modal>
      {shell.discardDialog}
    </>
  );
}
